import {
  GetApiPayloadByUrl,
  GetApiResponseByStatus,
  GetApiResponseByUrl,
} from '@crossbeam/openapi';
import { StrictOmit } from '@crossbeam/types';

import {
  BILLING_TIERS,
  CORE_SEAT_COMPONENTS,
  SALES_SEAT_COMPONENTS,
} from '@/constants/billing';

export type CoreSeatComponent =
  (typeof CORE_SEAT_COMPONENTS)[keyof typeof CORE_SEAT_COMPONENTS];
export type SalesSeatComponent =
  (typeof SALES_SEAT_COMPONENTS)[keyof typeof SALES_SEAT_COMPONENTS];

export type PortalInfo = GetApiResponseByUrl<
  '/v0.2/billing/portal/sync',
  'post'
>;

export type CreditPack =
  GetApiResponseByUrl<'/v0.1/credits/pricing/packs'>['packs'][number];
export type CreditPackPurchaseResponse = GetApiResponseByStatus<
  '/v0.1/credits/packs',
  'post',
  201
>;

export type PurchaseConnectorPayload = GetApiPayloadByUrl<
  '/v0.2/billing/subscriptions/{product_handle}',
  'post'
>;
export type PurchaseConnectorResponse = GetApiResponseByUrl<
  '/v0.2/billing/subscriptions/{product_handle}',
  'post'
>;
export type EditConnectorResponse = GetApiResponseByUrl<
  '/v0.2/billing/subscriptions/{product_handle}',
  'put'
>;

export type PurchaseComponentsResponse = GetApiResponseByUrl<
  '/v0.2/billing/subscription/components',
  'post'
>;

export type PreviewComponentsResponse = GetApiResponseByUrl<
  '/v0.2/billing/subscription/components/preview',
  'post'
>;
export type PreviewComponent = PreviewComponentsResponse['components'][number];

export type PreviewConnectorResponse = GetApiResponseByUrl<
  '/v0.2/billing/subscriptions/{product_handle}/preview',
  'post'
>;
export type PreviewConnectorPayload = GetApiPayloadByUrl<
  '/v0.2/billing/subscriptions/{product_handle}/preview',
  'post'
>;
export type PreviewProduct = PreviewConnectorResponse['products'][number];

export type PaymentInfo = GetApiResponseByUrl<'/v0.2/billing/payment'>;

export type BillingTier = (typeof BILLING_TIERS)[keyof typeof BILLING_TIERS];
export type BillingInfo = StrictOmit<
  GetApiResponseByUrl<'/v0.2/billing/sync', 'post'>,
  'billing_tier'
> & {
  billing_tier: BillingTier;
};
export type ChargifyComponent = NonNullable<
  BillingInfo['chargify_subscription']
>['components'][number];
export type PricePoint = ChargifyComponent['price_points'][number];

type PostSubscriptionPayload = GetApiPayloadByUrl<
  '/v0.2/billing/subscription/components',
  'post'
>;
export type PostSubscriptionComponent =
  PostSubscriptionPayload['components'][number];

export type BillingInteraction = {
  cta?: string;
  cta_2?: string;
  talkToSalesReason?: string;
  event_site: string;
  seatType?: string;
  isEarlyAdopter?: boolean;
  period?: string;
  number_of_seats?: number;
  attribution_credits?: string;
  isPlatformUpgrade?: boolean;
};

export type BillingQuery = {
  cancelDestination: string;
  userIds?: string[];
  emails?: string[];
  core?: number;
  sales?: number;
} | null;

export enum BillingPeriod {
  Month = 'month',
  Year = 'year',
}

export type UpgradePreviewResponse =
  GetApiResponseByUrl<'/v0.2/billing/subscription/upgrade/preview'>;

export type BillingSummaryLineType = {
  id: string;
  label: string;
  value: string;
  subLabel?: string;
  labelClass?: string;
  subLabelClass?: string;
  tooltip?: string;
  icon?: string;
  valueClass?: string;
  testId?: string;
  valueTestId?: string;
  hasDivider?: boolean;
};

export type BillingSummaryTotalType = {
  label: string;
  value: string;
  valueClass?: string;
  valueTestId?: string;
};

export type BillingSummaryPreviewProps = {
  lines: BillingSummaryLineType[];
  total?: BillingSummaryTotalType;
  title?: string;
  isLoading?: boolean;
  hasError?: boolean;
  footnoteClass?: string;
};
